import React, { useContext } from "react";
import useSWR from "swr";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Container, Grid, Skeleton, Typography } from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import ScheduleIcon from "@mui/icons-material/Schedule";
import Parser from "html-react-parser";
import { UserContextType, useAuth } from "../auth";
import { groupService } from "../service/GroupService";
import { GroupSummary } from "../types/group";
import { editorial, cardTexture } from "../common/editorialTheme";
import GroupMembershipAction from "./GroupMembershipAction";

export const groupCardSx = {
  p: { xs: 3, md: 4 },
  borderRadius: "8px",
  backgroundColor: editorial.paper,
  backgroundImage: cardTexture,
  border: `1px solid ${editorial.divider}`,
  boxShadow: editorial.cardShadow,
};

const Groups: React.FC = () => {
  const navigate = useNavigate();
  const { getToken } = useContext<UserContextType>(useAuth);
  const token = getToken ? getToken() : "";

  // The token is part of the key so the list refetches (with membership status) after login/logout.
  const { data, error, isLoading, mutate } = useSWR<GroupSummary[]>(["groups", token], () =>
    groupService.getGroups(token || undefined)
  );

  return (
    <Box sx={{ backgroundColor: editorial.canvas, minHeight: "100vh", pb: 10 }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 6 } }}>
        <Box sx={{ mb: 4 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: editorial.headlineFont,
              fontWeight: 600,
              color: editorial.primary,
              fontSize: { xs: "2rem", md: "2.75rem" },
              lineHeight: 1.15,
              letterSpacing: "-0.015em",
              mb: 1,
            }}
          >
            Groups
          </Typography>
          <Typography sx={{ fontFamily: editorial.bodyFont, color: editorial.textVariant, fontSize: "1.1rem" }}>
            Find a small group to grow, pray and do life together.
          </Typography>
        </Box>

        {error && <Alert severity="error">Failed to load groups.</Alert>}

        {isLoading && (
          <Grid container spacing={3}>
            {[0, 1, 2].map((i) => (
              <Grid item xs={12} md={6} key={i}>
                <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        )}

        {data && data.length === 0 && (
          <Box sx={groupCardSx}>
            <Typography sx={{ fontFamily: editorial.bodyFont, color: editorial.textVariant }}>
              There are no groups open right now. Check back soon!
            </Typography>
          </Box>
        )}

        {data && data.length > 0 && (
          <Grid container spacing={3}>
            {data.map((group) => (
              <Grid item xs={12} md={6} key={group.id}>
                <Box
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate(`/groups/${group.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") navigate(`/groups/${group.id}`);
                  }}
                  sx={{
                    ...groupCardSx,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: "0px 6px 16px rgba(30, 35, 34, 0.08)" },
                  }}
                >
                  <Typography
                    component="h2"
                    sx={{
                      fontFamily: editorial.headlineFont,
                      fontWeight: 600,
                      color: editorial.primary,
                      fontSize: "1.5rem",
                      mb: 1,
                    }}
                  >
                    {group.name}
                  </Typography>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2, color: editorial.secondary }}>
                    {group.meetingInfo && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <ScheduleIcon fontSize="small" />
                        <Typography sx={{ fontFamily: editorial.bodyFont, fontSize: "0.9rem" }}>{group.meetingInfo}</Typography>
                      </Box>
                    )}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <PeopleIcon fontSize="small" />
                      <Typography sx={{ fontFamily: editorial.bodyFont, fontSize: "0.9rem" }}>
                        {group.memberCount} {group.memberCount === 1 ? "member" : "members"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      flexGrow: 1,
                      mb: 2,
                      fontFamily: editorial.bodyFont,
                      color: editorial.textVariant,
                      display: "-webkit-box",
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      "& p": { lineHeight: 1.6, m: 0, mb: 1 },
                      "& img": { display: "none" },
                    }}
                  >
                    {Parser(group.description || "")}
                  </Box>

                  <GroupMembershipAction group={group} onChanged={() => mutate()} showViewButton />
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default Groups;
